import { useState } from 'react';
import { SkillArtIcon } from '../../../../components/skill-art-icon';
import { canonicalHorizontalShapeForSkillMdSize } from '../../../../lib/skill-item-art';
import { getShapeDimensions } from '../../../../lib/grid-logic';
import { mono, dmMono } from './tokens';

const PREVIEW_SLOT_PX = 36;

export default function SkillTile({
  skill,
  interactive = false,
  onClick,
}) {
  const [hovered, setHovered] = useState(false);
  const enabled = skill.enabled !== false;

  const shape =
    skill.shape ||
    canonicalHorizontalShapeForSkillMdSize(skill.skill_md_size ?? null) ||
    canonicalHorizontalShapeForSkillMdSize(null);
  const { cols } = getShapeDimensions(shape);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={interactive ? onClick : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '12px 14px',
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${hovered ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 8,
        opacity: enabled ? 1 : 0.5,
        cursor: interactive ? 'pointer' : 'default',
        transition: 'all 0.15s',
        boxShadow: hovered ? '0 0 14px rgba(255,255,255,0.04)' : 'none',
        minHeight: 140,
      }}
    >
      {(skill.is_first_party || skill.is_featured || skill.is_official) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {(skill.is_first_party || skill.is_official) && (
            <span
              style={{
                ...mono,
                fontSize: 9,
                fontWeight: 500,
                padding: '1px 6px',
                borderRadius: 4,
                background: 'rgba(74,222,128,0.15)',
                color: '#4ADE80',
                border: '1px solid rgba(74,222,128,0.3)',
              }}
            >
              Official
            </span>
          )}
          {skill.is_featured && (
            <span
              style={{
                ...mono,
                fontSize: 9,
                fontWeight: 500,
                padding: '1px 6px',
                borderRadius: 4,
                background: 'rgba(251,191,36,0.15)',
                color: '#FBBF24',
                border: '1px solid rgba(251,191,36,0.3)',
              }}
            >
              Featured
            </span>
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          justifyContent: 'space-between',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              ...dmMono,
              fontSize: 13,
              color: 'white',
              fontWeight: 600,
              wordBreak: 'break-word',
              lineHeight: 1.25,
            }}
          >
            {skill.slug || skill.name}
          </div>
          {skill.author ? (
            <div
              style={{
                ...mono,
                fontSize: 9,
                color: 'rgba(255,255,255,0.4)',
                marginTop: 2,
              }}
            >
              by {skill.author}
            </div>
          ) : null}
        </div>
        <div
          className="relative grid overflow-hidden"
          style={{
            gridTemplateColumns: `repeat(3, ${PREVIEW_SLOT_PX}px)`,
            gridTemplateRows: `${PREVIEW_SLOT_PX}px`,
            width: 3 * PREVIEW_SLOT_PX,
            minWidth: 3 * PREVIEW_SLOT_PX,
            height: PREVIEW_SLOT_PX,
            flexShrink: 0,
            boxSizing: 'content-box',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 4,
          }}
        >
          {Array.from({ length: 3 }).map((_, idx) => (
            <div
              key={`bg-${idx}`}
              style={{
                border: '1px solid rgba(255,255,255,0.06)',
                background: 'rgba(255,255,255,0.02)',
              }}
            />
          ))}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              gridColumn: `1 / span ${cols}`,
              gridRow: '1 / span 1',
              overflow: 'hidden',
            }}
          >
            <SkillArtIcon
              skillId={skill.slug || skill.name || 'unknown'}
              category={skill.category || null}
              skillMdSize={skill.skill_md_size ?? null}
              shape={shape}
              label={skill.slug || skill.name}
              className="h-full w-full shrink-0 border-0 bg-transparent"
            />
          </div>
        </div>
      </div>

      {skill.description ? (
        <div
          style={{
            ...mono,
            fontSize: 11,
            color: 'rgba(255,255,255,0.6)',
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {skill.description}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          ...mono,
          fontSize: 9,
          color: 'rgba(255,255,255,0.4)',
        }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          {typeof skill.stars === 'number' ? <span>★ {skill.stars}</span> : null}
          {typeof skill.install_count === 'number' ? (
            <span>⬇ {skill.install_count}</span>
          ) : null}
        </div>
        <span
          style={{
            color: enabled ? '#4ade80' : 'rgba(255,255,255,0.3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: 8,
          }}
        >
          {enabled ? 'Equipped' : 'Disabled'}
        </span>
      </div>
    </div>
  );
}
